import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  fullDate,
  get,
  italic,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  fullUrl,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  doiUrl,
  nlmDate,
  nlmName,
  organizationOrNothing,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A clinical practice guideline — a NICE, WHO, USPSTF or professional-society document
 * on the web, or a guideline printed in a journal — in APA 7, AMA 11 and Vancouver (NLM).
 *
 * Sources checked (October 2026):
 * - APA 7: no guideline type of its own; it is a report by a group author (Publication
 *   Manual §10.4) — the agency or committee that wrote it, the italic title, the
 *   guideline number in parentheses, the site name only when it differs from the
 *   author, and the URL. Curtin University's APA 7 guide: "National Institute for
 *   Health and Care Excellence. (2020). Joint replacement (primary): Hip, knee and
 *   shoulder (NICE Guideline NG157). https://www.nice.org.uk/guidance/NG157". A group
 *   author is spelled out in the reference list and abbreviated in the text from the
 *   first citation: (National Institute for Health and Care Excellence [NICE], 2026),
 *   then (NICE, 2026). A guideline printed in a journal is a journal article. APA lists
 *   up to 20 authors; with 21 or more, the first 19, an ellipsis and the last.
 * - AMA 11: a guideline in a journal is cited as a journal article ("Clinical practice
 *   guidelines should be cited as journal articles whenever possible" — Western New
 *   England University's AMA guide): "Hopewell S, Chan AW, Collins GS, et al. CONSORT
 *   2025 statement: … BMJ. 2025;389:e081123. doi:10.1136/bmj-2024-081123". AMA lists up
 *   to six authors and, from seven, the first three and et al. A web document follows
 *   AMA's website pattern: organization, title, Published, Updated and Accessed dates,
 *   URL (as in "International Committee of Medical Journal Editors. Recommendations …
 *   Accessed September 16, 2026. https://www.icmje.org/recommendations/"). Putting the
 *   guideline's number in a sentence after the title is the generator's choice, not an
 *   AMA example.
 * - Vancouver (NLM, Citing Medicine): an Internet monograph with an organization as
 *   author — "Title [Internet]. Place: Publisher; date [updated …; cited …]. (Series).
 *   Available from: URL". NLM prints dates as "2019 Aug 28" and, in the samples reviewed
 *   (ICMJE/NLM "Samples of Formatted References"), lists the first six authors then et
 *   al. A journal guideline is a journal article: "Whelton PK, … Hypertension. 2018
 *   Jun;71(6):e13-e115. doi: 10.1161/HYP.0000000000000065." The ICMJE Recommendations
 *   say to cite the guideline's own document, not a guideline aggregator: the National
 *   Guideline Clearinghouse closed in 2018.
 * - Facts used in the worked examples: NICE guideline NG136, Hypertension in adults:
 *   diagnosis and management, published 28 August 2019, last updated 26 February 2026
 *   (nice.org.uk/guidance/ng136, opened 16 October 2026); the 2017 ACC/AHA hypertension
 *   guideline, Hypertension 2018 Jun;71(6) (Crossref, DOI 10.1161/HYP.0000000000000065).
 */

export const GUIDELINE_FIELDS = {
  organization: "organization",
  authors: "authors",
  title: "title",
  abbreviation: "abbreviation",
  published: "published",
  updated: "updated",
  accessed: "accessed",
  url: "url",
  series: "series",
  publisher: "publisher",
  place: "place",
  journal: "journal",
  journalAbbr: "journalAbbr",
  volume: "volume",
  issue: "issue",
  pages: "pages",
  doi: "doi",
} as const;

const F = GUIDELINE_FIELDS;

/** A document on the issuing body's website, or a guideline printed in a journal. */
export type GuidelineKind = "online" | "journal";

export interface GuidelineLabels {
  apa: string;
  ama: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  vancouver: string;
  laterParenthetical: string;
  laterNarrative: string;
  apaNote: string;
  amaNote: string;
  vancouverNote: string;
}

interface Guideline {
  kind: GuidelineKind;
  authors: PersonName[];
  title: string;
  abbreviation: string;
  published: PartialDate | null;
  updated: PartialDate | null;
  accessed: PartialDate | null;
  url: string;
  series: string;
  publisher: string;
  place: string;
  journal: string;
  journalAbbr: string;
  volume: string;
  issue: string;
  pages: string;
  doi: string;
}

function readGuideline(kind: GuidelineKind, values: MediaValues): Guideline {
  const organization = organizationOrNothing(get(values, F.organization));
  const online = kind === "online";
  const doi = doiUrl(get(values, F.doi)) ?? "";
  return {
    kind,
    authors:
      organization.length > 0
        ? organization
        : parsePeople(get(values, F.authors)),
    title: get(values, F.title),
    abbreviation: get(values, F.abbreviation),
    published: parsePartialDate(get(values, F.published)),
    updated: online ? parsePartialDate(get(values, F.updated)) : null,
    accessed: online ? parsePartialDate(get(values, F.accessed)) : null,
    url: online ? fullUrl(get(values, F.url)) : doi,
    series: online ? get(values, F.series) : "",
    publisher: online ? get(values, F.publisher) : "",
    place: online ? get(values, F.place) : "",
    journal: online ? "" : get(values, F.journal),
    journalAbbr: online ? "" : get(values, F.journalAbbr),
    volume: online ? "" : get(values, F.volume),
    issue: online ? "" : get(values, F.issue),
    pages: online ? "" : get(values, F.pages),
    doi: online ? "" : doi,
  };
}

const RE_DOI_HOST = /^https?:\/\/doi\.org\//i;
const RE_PAGE_DASH = /\s*[-–—]\s*/;
const RE_DIGITS = /^\d+$/;

function bareDoi(doi: string): string {
  return doi.replace(RE_DOI_HOST, "");
}

/** The latest date on the page — what a reader gets today. */
function versionDate(guideline: Guideline): PartialDate | null {
  return guideline.updated ?? guideline.published;
}

/** NLM page range: unchanged leading digits are dropped from numbers only. */
export function nlmPages(text: string): string {
  const [from, to] = text.trim().split(RE_PAGE_DASH);
  if (!to) {
    return from ?? "";
  }
  if (RE_DIGITS.test(from) && RE_DIGITS.test(to) && from.length === to.length) {
    let shared = 0;
    while (shared < from.length - 1 && from[shared] === to[shared]) {
      shared += 1;
    }
    return `${from}-${to.slice(shared)}`;
  }
  return `${from}-${to}`;
}

/** AMA page range: the full numbers, hyphenated ("e13-e115"). */
function amaPages(text: string): string {
  const [from, to] = text.trim().split(RE_PAGE_DASH);
  return to ? `${from}-${to}` : (from ?? "");
}

function apaPages(text: string): string {
  const [from, to] = text.trim().split(RE_PAGE_DASH);
  return to ? `${from}–${to}` : (from ?? "");
}

/* ---------------------------------------------------------------------- APA */

function apaTitle(guideline: Guideline): string {
  return capitalizeSubtitle(guideline.title);
}

function apaOnline(guideline: Guideline): CitationParts {
  const authors = apaAuthors(guideline.authors);
  const year = versionDate(guideline)?.year ?? "n.d.";
  const title = apaTitle(guideline);
  const site = apaSiteName(guideline);
  const series = guideline.series ? ` (${guideline.series})` : "";
  const tail = [site ? `${site}${closer(site)}` : "", guideline.url]
    .filter(Boolean)
    .join(" ");
  const after = `${series}.${tail ? ` ${tail}` : ""}`;
  return tidyParts([
    plain(`${authors}${closer(authors)} (${year}). `),
    italic(title),
    plain(after),
  ]);
}

/** The site name is dropped when it is the author's own name or abbreviation. */
function apaSiteName(guideline: Guideline): string {
  const [first] = guideline.authors;
  const same = new Set([first?.family ?? "", guideline.abbreviation]);
  return same.has(guideline.publisher) ? "" : guideline.publisher;
}

function apaJournal(guideline: Guideline): CitationParts {
  const authors = apaAuthors(guideline.authors);
  const year = guideline.published?.year ?? "n.d.";
  const title = apaTitle(guideline);
  const volume = guideline.volume ? `, ${guideline.volume}` : "";
  const issue = guideline.issue ? `(${guideline.issue})` : "";
  const pages = guideline.pages ? `, ${apaPages(guideline.pages)}` : "";
  const doi = guideline.doi ? ` ${guideline.doi}` : "";
  return tidyParts([
    plain(`${authors}${closer(authors)} (${year}). ${title}${closer(title)} `),
    italic(`${guideline.journal}${volume}`),
    plain(`${issue}${pages}.${doi}`),
  ]);
}

function apaOutput(
  guideline: Guideline,
  labels: GuidelineLabels
): CitationOutput {
  const year =
    guideline.kind === "online"
      ? (versionDate(guideline)?.year ?? "n.d.")
      : (guideline.published?.year ?? "n.d.");
  const authors = guideline.authors;
  const reference =
    guideline.kind === "online" ? apaOnline(guideline) : apaJournal(guideline);
  const entries = [{ label: labels.referenceList, parts: reference }];
  if (authors.length > 0) {
    const group = authors[0].organization === true && guideline.abbreviation;
    const full = apaInTextAuthors(authors);
    const first = group ? `${full} [${guideline.abbreviation}]` : full;
    const narrativeFull = apaInTextAuthors(authors, true);
    const narrativeFirst = group
      ? `${narrativeFull} [${guideline.abbreviation}]`
      : narrativeFull;
    entries.push(
      { label: labels.parenthetical, parts: [plain(`(${first}, ${year})`)] },
      { label: labels.narrative, parts: [plain(`${narrativeFirst} (${year})`)] }
    );
    if (group) {
      entries.push(
        {
          label: labels.laterParenthetical,
          parts: [plain(`(${guideline.abbreviation}, ${year})`)],
        },
        {
          label: labels.laterNarrative,
          parts: [plain(`${guideline.abbreviation} (${year})`)],
        }
      );
    }
  }
  return { key: "apa", label: labels.apa, entries, note: labels.apaNote };
}

/* ---------------------------------------------------------------------- AMA */

function numberedAuthors(
  names: PersonName[],
  limit: number,
  shown: number
): string {
  if (names.length === 0) {
    return "";
  }
  const formatted = names.map(nlmName);
  if (names.length > limit) {
    return `${formatted.slice(0, shown).join(", ")}, et al.`;
  }
  return withTerminalPeriod(formatted.join(", "));
}

function withTerminalPeriod(text: string): string {
  return text.endsWith(".") ? text : `${text}.`;
}

function amaOnline(guideline: Guideline): string {
  const [first] = guideline.authors;
  const site = apaSiteName(guideline) && guideline.publisher !== first?.family;
  const pieces = [
    numberedAuthors(guideline.authors, 6, 3),
    withTerminalPeriod(guideline.title),
    guideline.series ? withTerminalPeriod(guideline.series) : "",
    site ? withTerminalPeriod(guideline.publisher) : "",
    guideline.published ? `Published ${fullDate(guideline.published)}.` : "",
    guideline.updated ? `Updated ${fullDate(guideline.updated)}.` : "",
    guideline.accessed ? `Accessed ${fullDate(guideline.accessed)}.` : "",
    guideline.url,
  ];
  return pieces.filter(Boolean).join(" ");
}

function amaJournal(guideline: Guideline): CitationParts {
  const head = [
    numberedAuthors(guideline.authors, 6, 3),
    withTerminalPeriod(guideline.title),
  ]
    .filter(Boolean)
    .join(" ");
  const year = guideline.published ? String(guideline.published.year) : "";
  const issue = guideline.issue ? `(${guideline.issue})` : "";
  const volume = guideline.volume ? `${guideline.volume}${issue}` : "";
  const pages = guideline.pages ? `:${amaPages(guideline.pages)}` : "";
  const locator = `${year}${year && volume ? ";" : ""}${volume}${pages}`;
  const doi = guideline.doi ? ` doi:${bareDoi(guideline.doi)}` : "";
  return tidyParts([
    plain(`${head} `),
    italic(guideline.journalAbbr || guideline.journal),
    plain(`.${locator ? ` ${locator}.` : ""}${doi}`),
  ]);
}

function amaOutput(
  guideline: Guideline,
  labels: GuidelineLabels
): CitationOutput {
  const parts =
    guideline.kind === "online"
      ? [plain(amaOnline(guideline))]
      : amaJournal(guideline);
  return {
    key: "ama",
    label: labels.ama,
    entries: [{ label: labels.referenceList, parts }],
    note: labels.amaNote,
  };
}

/* ---------------------------------------------------------------- Vancouver */

function vancouverOnline(guideline: Guideline): string {
  const imprint = [guideline.place, guideline.publisher].every(Boolean)
    ? `${guideline.place}: ${guideline.publisher}`
    : guideline.publisher;
  const brackets = [
    guideline.updated ? `updated ${nlmDate(guideline.updated)}` : "",
    guideline.accessed ? `cited ${nlmDate(guideline.accessed)}` : "",
  ].filter(Boolean);
  const dated = [
    nlmDate(guideline.published),
    brackets.length > 0 ? `[${brackets.join("; ")}]` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const publication = [imprint, dated].filter(Boolean).join("; ");
  const pieces = [
    numberedAuthors(guideline.authors, 6, 6),
    `${guideline.title} [Internet].`,
    publication ? `${publication}.` : "",
    guideline.series ? `(${guideline.series}).` : "",
    guideline.url ? `Available from: ${guideline.url}` : "",
  ];
  return pieces.filter(Boolean).join(" ");
}

function vancouverJournal(guideline: Guideline): string {
  const issue = guideline.issue ? `(${guideline.issue})` : "";
  const volume = guideline.volume ? `${guideline.volume}${issue}` : "";
  const date = nlmDate(guideline.published);
  const pages = guideline.pages ? `:${nlmPages(guideline.pages)}` : "";
  const locator = `${date}${date && volume ? ";" : ""}${volume}${pages}`;
  const doi = guideline.doi ? ` doi: ${bareDoi(guideline.doi)}.` : "";
  const pieces = [
    numberedAuthors(guideline.authors, 6, 6),
    withTerminalPeriod(guideline.title),
    `${guideline.journalAbbr || guideline.journal}.`,
    locator ? `${locator}.` : "",
  ];
  return `${pieces.filter(Boolean).join(" ")}${doi}`;
}

function vancouverOutput(
  guideline: Guideline,
  labels: GuidelineLabels
): CitationOutput {
  const text =
    guideline.kind === "online"
      ? vancouverOnline(guideline)
      : vancouverJournal(guideline);
  return {
    key: "vancouver",
    label: labels.vancouver,
    entries: [{ label: labels.referenceList, parts: [plain(text)] }],
    note: labels.vancouverNote,
  };
}

export function guidelineIsReady(values: MediaValues): boolean {
  return (
    get(values, F.title) !== "" &&
    (get(values, F.organization) !== "" || get(values, F.authors) !== "")
  );
}

export function formatGuideline(
  kind: GuidelineKind,
  values: MediaValues,
  labels: GuidelineLabels
): CitationOutput[] {
  const guideline = readGuideline(kind, values);
  return [
    apaOutput(guideline, labels),
    amaOutput(guideline, labels),
    vancouverOutput(guideline, labels),
  ];
}
